/**
 * The item being edited, drawn as it will be made.
 *
 * A parameter dialog is a column of numbers, and numbers about geometry are
 * hard to be sure of. Choosing "Rectangular thru Pad" and seeing the stadium
 * stay a stadium is a bug you can only find by closing the dialog and looking —
 * which is exactly how it was found, weeks after it was introduced. A picture
 * beside the fields turns that into something you cannot miss.
 *
 * Everything here is drawn from {@link padOutline}, the same function the
 * renderer and the Gerber writer use. That is the point: a preview with
 * geometry of its own would be a second opinion, and a second opinion that
 * disagreed would be worse than none. Where the plot swells the mask by half
 * the file's `maskPadSwell`, so does this.
 */

import {
  isElectrical, padIsThrough,
  type CadDocument, type Entity, type PadEntity,
} from '@sircad/core/model.ts';
import { occupiedLayers } from '@sircad/core/geom/board.ts';
import { padOutline, padShape } from '@sircad/core/geom/pad.ts';
import { Renderer } from '../render/renderer.ts';
import { Viewport } from '../render/viewport.ts';
import { worldBounds } from '../render/bounds.ts';

/** Colours, near enough to a real board to read at a glance. */
const INK = {
  ground: '#101418',
  mask: '#b3322c',
  copper: '#d9a441',
  hole: '#0b0e11',
  wall: '#7d858c',
  axis: '#2b3440',
  label: '#e8edf2',
};

export interface Preview {
  el: HTMLCanvasElement;
  /** Redraw from the entity as it now stands. */
  update(): void;
}

/**
 * A pad, face on: mask opening, copper, and the hole through it.
 *
 * Sized to the pad each time it is drawn, so growing a pad in the dialog does
 * not walk it out of the frame.
 */
export function padPreview(pad: PadEntity, doc: CadDocument): Preview {
  const el = document.createElement('canvas');
  el.className = 'edit-preview';
  el.width = 260;
  el.height = 200;

  const update = (): void => {
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const w = el.width;
    const h = el.height;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = INK.ground;
    ctx.fillRect(0, 0, w, h);

    // Half the file's swell per side, as the plot takes it, and the pad's own
    // margin where it has one.
    const swell = (doc.gerberOut?.maskPadSwell ?? 0.004) / 2;
    const margin = pad.maskMargin ?? swell;

    const copper = padOutline({ ...pad, x: 0, y: 0 });
    const mask = padOutline({ ...pad, x: 0, y: 0 }, Math.max(0, margin));
    if (copper.length === 0) return;

    // Fit the larger of the two, with room to breathe.
    let r = 0;
    for (const p of [...copper, ...mask]) r = Math.max(r, Math.abs(p.x), Math.abs(p.y));
    if (!(r > 0)) r = 0.05;
    const scale = (Math.min(w, h) / 2) / (r * 1.35);
    const sx = (x: number): number => w / 2 + x * scale;
    const sy = (y: number): number => h / 2 - y * scale;

    // The insertion point, so an offset pad reads as offset.
    ctx.strokeStyle = INK.axis;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, sy(0)); ctx.lineTo(w, sy(0));
    ctx.moveTo(sx(0), 0); ctx.lineTo(sx(0), h);
    ctx.stroke();

    const fill = (pts: { x: number; y: number }[], colour: string): void => {
      if (pts.length < 3) return;
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.moveTo(sx(pts[0]!.x), sy(pts[0]!.y));
      for (let i = 1; i < pts.length; i++) ctx.lineTo(sx(pts[i]!.x), sy(pts[i]!.y));
      ctx.closePath();
      ctx.fill();
    };

    // Mask first, copper over it: what you see through the opening is copper,
    // and what surrounds it is mask.
    if (margin > 0) fill(mask, INK.mask);
    fill(copper, INK.copper);

    // The hole, and a slot when the hole is routed rather than drilled.
    const hole = padIsThrough(pad.type) ? (pad.hole ?? 0) : 0;
    if (hole > 0) {
      const rr = (hole / 2) * scale;
      ctx.fillStyle = INK.hole;
      ctx.strokeStyle = INK.wall;
      ctx.lineWidth = 1;
      ctx.beginPath();
      if (pad.slot) {
        // `slot` is one end; the other follows by symmetry about the centre.
        const ax = sx(pad.slot.x - pad.x);
        const ay = sy(pad.slot.y - pad.y);
        const bx = sx(-(pad.slot.x - pad.x));
        const by = sy(-(pad.slot.y - pad.y));
        const a = Math.atan2(by - ay, bx - ax);
        ctx.arc(ax, ay, rr, a + Math.PI / 2, a - Math.PI / 2);
        ctx.arc(bx, by, rr, a - Math.PI / 2, a + Math.PI / 2);
        ctx.closePath();
      } else {
        ctx.arc(sx(0), sy(0), rr, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.stroke();
    }

    // What it is, in words, because a shape can be ambiguous at this size.
    const shape = padShape(pad).kind;
    const pin = pad.pin ? `pin ${pad.pin} · ` : '';
    ctx.fillStyle = INK.label;
    ctx.font = '11px ui-monospace, monospace';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`${pin}${shape}${hole > 0 ? ' · drilled' : ''}`, 8, h - 7);
  };

  update();
  return { el, update };
}

/**
 * Anything else, drawn by the renderer itself.
 *
 * A hand-drawn picture per entity kind would be six more chances to disagree
 * with the board. Instead the entity is put alone in a copy of its own document
 * — same layer table, same colours, same draw modes — and the real renderer is
 * pointed at it. What you see in the dialog is then the same code that drew
 * what is behind the dialog, which is the only way a preview can be trusted.
 */
export function entityPreview(entity: Entity, doc: CadDocument): Preview {
  const el = document.createElement('canvas');
  el.className = 'edit-preview';
  el.width = 260;
  el.height = 200;

  const viewport = new Viewport();
  const renderer = new Renderer(el, viewport);

  const update = (): void => {
    viewport.width = el.width;
    viewport.height = el.height;
    viewport.dpr = 1;

    const box = worldBounds(entity);
    if (box) {
      // A little air round it, and a floor on the span so a zero-length line or
      // a dot does not ask for infinite magnification.
      const pad = Math.max((box.x2 - box.x1), (box.y2 - box.y1), 0.02) * 0.2;
      viewport.fit(box.x1 - pad, box.y1 - pad, box.x2 + pad, box.y2 + pad);
    }

    renderer.render({ ...doc, entities: [entity] }, { images: [] });
  };

  update();
  return { el, update };
}

